import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Table, Spin, Alert, Input, Space, Button, Typography, Tooltip, Tag, Select, message, Dropdown, Checkbox } from 'antd';
import { DownloadOutlined, SearchOutlined, EyeOutlined } from '@ant-design/icons';
import { API_BASE_URL as SHARED_API_BASE_URL } from '../../../utils/config';
import { formatCellValue } from '../../../utils/tableFilters';
import { formatMoney } from '../../../utils/currency';
import { trLower, trIncludes } from '../../../utils/trText';
import axios from 'axios';
import { useTheme } from '../../../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import FormikForm from '../../form/FormikForm';
import { useAuth } from '../../../context/AuthContext';
import {
  EditOutlined,
  PlusOutlined,
  PaperClipOutlined,
  FileTextOutlined
} from '@ant-design/icons';

const API_BASE_URL = SHARED_API_BASE_URL;

const { Text } = Typography;
const { Option } = Select;
function getSafeArrayFromResponse(responseData, dataKey) {
  if (Array.isArray(responseData)) {
    return responseData;
  }
  console.log('Hiçbir array bulunamadı, boş array döndürülüyor');
  return [];
}

function autoInferColumnsFromItems(items, maxColumns = 8) {
  if (!items || items.length === 0) return [];
  const first = items.find((it) => it && typeof it === 'object') || items[0];
  if (!first || typeof first !== 'object') {
    return [
      {
        title: 'Değer',
        dataIndex: 'value',
        key: 'value',
        render: (_, __, index) => formatCellValue(items[index])
      }
    ];
  } 
  const keys = Object.keys(first).slice(0, maxColumns);
  return keys.map((key) => ({
    title: key,
    dataIndex: key,
    key,
    render: (val) => formatCellValue(val)
  }));
}

// 26.10 düzeltmesi: anahtar yoksa her render'da rastgele anahtar üretiliyordu (satırlar
// her seferinde yeniden kuruluyor, sıralama/sayfalama titriyordu). Sözleşme satırları
// 'ID' (büyük harf) taşır; hiçbiri yoksa satırın değerlerinden kararlı bir anahtar kurulur.
function getRowKey(item) {
  if (item === null || item === undefined) return 'row-bos';
  if (typeof item !== 'object') return `row-${String(item)}`;
  const anahtar = item.id ?? item.ID ?? item._id ?? item.key ?? item.uuid ?? item.maptriksId ?? item.maptriksID;
  if (anahtar !== null && anahtar !== undefined && anahtar !== '') return String(anahtar);
  return `row-${Object.values(item).filter(v => v === null || typeof v !== 'object').join('|')}`;
}

// 26.10 düzeltmesi (F1): modal tablolarında sıralama. Tarihler 'GG/AA/YYYY', 'GG.AA.YYYY'
// ya da ISO gelebiliyor; metin olarak sıralanınca '14/02/2025' > '05/06/2026' oluyordu.
const SIRALAMA_TARIH_ISO = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/;
const SIRALAMA_TARIH_GAY = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})/;
function siralamaDegeri(deger) {
  if (deger === null || deger === undefined || typeof deger === 'object') return null;
  if (typeof deger === 'number') return Number.isFinite(deger) ? deger : null;
  if (typeof deger === 'boolean') return deger ? 1 : 0;
  const s = String(deger).trim();
  if (s === '' || s === '-' || s.startsWith('0000') || s.startsWith('00/00')) return null;
  let m = s.match(SIRALAMA_TARIH_ISO);
  if (m) return Number(m[1]) * 10000 + Number(m[2]) * 100 + Number(m[3]);
  m = s.match(SIRALAMA_TARIH_GAY);
  if (m) return Number(m[3]) * 10000 + Number(m[2]) * 100 + Number(m[1]);
  if (/^-?\d+(\.\d+)?$/.test(s)) return parseFloat(s);
  return s;
}
// yon: antd'nin üçüncü argümanı ('ascend' | 'descend'); boş değerler her iki yönde de sonda kalır
function siralaKarsilastir(a, b, yon) {
  const x = siralamaDegeri(a);
  const y = siralamaDegeri(b);
  const sonda = yon === 'descend' ? -1 : 1;
  if (x === null && y === null) return 0;
  if (x === null) return sonda;
  if (y === null) return -sonda;
  if (typeof x === 'number' && typeof y === 'number') return x - y;
  return String(x).localeCompare(String(y), 'tr', { numeric: true, sensitivity: 'base' });
}
// Kolonda sorter tanımlı değilse dataIndex değerine göre sıralayıcı ekler. Aksiyon / dosya
// kolonları (satırlarda o alanda hiç düz değer yok) atlanır.
function siralanabilirKolon(col, satirlar) {
  if (!col || col.sorter !== undefined || !col.dataIndex || col.noSort) return col;
  const alan = col.dataIndex;
  if (!(satirlar || []).some(r => siralamaDegeri(r?.[alan]) !== null)) return col;
  return { ...col, sorter: (a, b, yon) => siralaKarsilastir(a?.[alan], b?.[alan], yon) };
}

// Kayıtlı kolon seçimi olan kullanıcılara bir kez eklenecek, sonradan gelen kolon anahtarları
const SONRADAN_EKLENEN_KOLONLAR = ['Kayıt Tipi'];

export default function DataListModal({
  open,
  onClose,
  apiUrl,
  title = 'Veri Listesi',
  queryParams,
  dataKey,
  columns,
  transformData,
  width,
  data
}) {
  const { user } = useAuth();
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedBank, setSelectedBank] = useState('all');
  const [selectedFaturaTuru, setSelectedFaturaTuru] = useState('all');
  const [selectedKayitTipi, setSelectedKayitTipi] = useState('all');
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [currentFormId, setCurrentFormId] = useState(null);
  const [modalTitle, setModalTitle] = useState('Form');
  const [currentUniqueValue, setCurrentUniqueValue] = useState(null);
  const [currentUniqueField, setCurrentUniqueField] = useState('');
  const [currentTableName, setCurrentTableName] = useState('');
  const [formExtraData, setFormExtraData] = useState({});
  const [bankaSozlesmeleri, setBankaSozlesmeleri] = useState([]);
  const [yerSozlesmeleri, setYerSozlesmeleri] = useState([]);
  const [sozlesmelerLoading, setSozlesmelerLoading] = useState(false);
  const [selectedLokasyonId, setSelectedLokasyonId] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [selectedBankaColumns, setSelectedBankaColumns] = useState([]);
  const [selectedYerColumns, setSelectedYerColumns] = useState([]);
  const [columnSelectDropdownOpen, setColumnSelectDropdownOpen] = useState(false);
  const [bankaColumnSelectDropdownOpen, setBankaColumnSelectDropdownOpen] = useState(false);
  const [yerColumnSelectDropdownOpen, setYerColumnSelectDropdownOpen] = useState(false);

  const openFormModal = useCallback(async (formId, title = 'Form', tableName = '', uniqueField = '', uniqueValue = null, extraData = {}, overrideFunction = null) => {
    setModalTitle(title);
    setCurrentFormId(formId);
    setCurrentTableName(tableName);
    setCurrentUniqueField(uniqueField);
    setCurrentUniqueValue(uniqueValue);
    setFormExtraData({
      ...extraData,
      ...(overrideFunction && { _override_function: overrideFunction })
    });
    setFormModalOpen(true);
  }, []);

  const handleEditSunum = useCallback((record) => {
    const extraData = {
      ekleyen: user?.kullanici_no,
      eklenmeTarihi: new Date().toISOString().split('T')[0],
    }
    const isSunum = record?.Tipi === "Sunum";
    const formId = isSunum ? 201 : 83;
    const tableName = isSunum ? 'sunumlar' : 'banka_talep';
    const formTitle = isSunum ? 'Sunum Düzenle' : 'Talep Düzenle';
    
    if (!isSunum && queryParams?.maptriksId) {
      extraData.lokasyon = queryParams.maptriksId;
    }
    
    const operation = record?.id ? 'Update' : 'Insert';
    openFormModal(formId, formTitle, tableName, 'id', record?.id, extraData, operation);
  }, [openFormModal, user, queryParams]);

  const handleEditInsaatMaliyet = useCallback((record) => {
    const extraData = {
      kayit_tarihi: new Date().toISOString().split('T')[0],
    }
    const recordId = record?.id;
    openFormModal(78, 'Masraf Düzenle', 'maliyetler', 'id', recordId, extraData, 'Update');
  }, [openFormModal]);

  // 'Platform Fiyatı' satırları platformlar.fiyat'tan services/platform_maliyet.py ile
  // üretilir ve platform formundan güncellenir; burada düzenlenmez/silinmez.
  const isPlatformFiyat = (record) =>
    record?.masraf_adi === 'Platform Fiyatı' && String(record?.aciklama || '').startsWith('[PLATFORM-FIYAT]');

  const handleAddFatura = useCallback((record) => {
    const extraData = {
      root: "masraf-fatura",
      dir: "masraf-fatura",
      rootID: record?.id,
      lokasyon: queryParams?.maptriksId,
      ekleyen: user?.kullanici_no,
      eklenmeTarihi: new Date().toISOString().split('T')[0],
    }
    const recordId = record?.platformID;
    openFormModal(189, 'Fatura Ekle', 'dosyalar', 'rootID', recordId, extraData, 'Insert');
  }, [openFormModal, user, queryParams]);

  const closeFormModal = useCallback(() => {
    setFormModalOpen(false);
    setCurrentFormId(null);
    setModalTitle('Form');
    setCurrentUniqueValue(null);
    setCurrentUniqueField('');
    setCurrentTableName('');
    setFormExtraData({});
  }, []);

  const handleFormSuccess = useCallback((result) => {
    setFormModalOpen(false);
    setCurrentFormId(null);
    setModalTitle('Form');
    setCurrentUniqueValue(null);
    setCurrentUniqueField('');
    setCurrentTableName('');
    setFormExtraData({});
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const handleCreatePDFSunum = useCallback(async (record) =>  {
    try {
      const maptriksId = record?.maptriksId || record?.maptriksID || record?.MaptriksID;
      
      if (!maptriksId) {
        alert('Lokasyon ID bulunamadı!');
        return;
      }
      
      const hide = message.loading('PDF sunum oluşturuluyor...', 0);
      
      try {
        const response = await axios.post(
          `${API_BASE_URL}/sunum/generate-pdf-sunum/${maptriksId}`,
          {},
          {
            headers: {
              'Content-Type': 'application/json',
              ...(localStorage.getItem('auth_token') && {
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
              })
            }
          }
        );
        
        hide();
        
        if (response.data?.success) {
          message.success('PDF sunum başarıyla oluşturuldu!');
          setRefreshTrigger(prev => prev + 1);
        } else {
          message.error('PDF sunum oluşturulurken bir hata oluştu.');
        }
      } catch (error) {
        hide();
        console.error('PDF sunum oluşturma hatası:', error);
        message.error(error?.response?.data?.detail || 'PDF sunum oluşturulurken bir hata oluştu.');
      }
    } catch (error) {
      console.error('PDF sunum oluşturma hatası:', error);
      message.error(error?.response?.data?.detail || 'PDF sunum oluşturulurken bir hata oluştu.');
    }
  }, []);

  const handleDownloadPDFSunum = useCallback(async (record) => {
    try {
      const maptriksId = record?.maptriksId || record?.maptriksID || record?.MaptriksID;
      
      if (!maptriksId) {
        message.error('Lokasyon ID bulunamadı!');
        return;
      }
      const hide = message.loading('PDF sunum indiriliyor...', 0);
      
      try {
        const response = await axios.get(
          `${API_BASE_URL}/sunum/download-pdf-sunum/${maptriksId}`,
          {
            responseType: 'blob',
            headers: {
              ...(localStorage.getItem('auth_token') && {
                'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
              })
            }
          }
        );

        hide();
        
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        const contentDisposition = response.headers['content-disposition'];
        let filename = `${maptriksId}.pdf`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/^["']|["']$/g, '');
          }
        }
        
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        message.success('PDF sunum başarıyla indirildi!');
      } catch (error) {
        hide();
        console.error('PDF sunum indirme hatası:', error);
        message.error(error?.response?.data?.detail || 'PDF sunum indirilirken bir hata oluştu.');
      }
    } catch (error) {
      console.error('PDF sunum indirme hatası:', error);
      message.error('PDF sunum indirilirken bir hata oluştu.');
    }
  }, []);

  const handleCreateSunum = useCallback((record) => {
    const extraData = {
      maptriksId : record.maptriksId,
      ekleyen : user?.kullanici_no,
      eklenmeTarihi : new Date().toISOString().split('T')[0],
    }
    const lokasyonId = record.maptriksId;
    if (lokasyonId) {
      openFormModal(199, 'Sunum Ekle', 'sunumlar', {}, {}, extraData, 'Insert');
    } else {
      alert('Lokasyon ID bulunamadı!');
    }
  }, []);

  const handleAddTalep = useCallback((record) => {
    const id = record?.maptriksId;
    console.log("ID", id);
    const extraData = {
      lokasyon: record?.maptriksId,
      ekleyen: user?.kullanici_no
    };
    console.log("Extra Data", extraData);
    openFormModal(83, 'Talep Ekle', 'banka_talep', 'lokasyon', {}, extraData, 'Insert');
  }, [openFormModal]);

  const handleAddInsaatMaliyet = useCallback((record) => {
    const extraData = {
      maptriksID: record?.maptriksId
    }
    openFormModal(79, 'Masraf Ekle', 'maliyetler', 'maptriksID', {}, extraData, 'Insert');
  }, [openFormModal]);

  const handleAddFirmaMaliyet = useCallback(() => {
    openFormModal(208, 'Firma Ekle', 'firmalar', '', {}, {}, 'Insert');
  }, [openFormModal]);

  const handleAddTahminiMaliyet = useCallback((record) => {
    openFormModal(214, 'Tahmini Maliyet Ekle', 'tahmini_maliyetler', 'maptriksID', record?.maptriksID || queryParams?.maptriksId, {}, 'Insert');
  }, [openFormModal, queryParams]);

  const handleEditTahminiMaliyet = useCallback((record) => {
    const extraData = {
      ekleyen: user?.kullanici_no, 
      eklenmeTarihi: new Date().toISOString().split('T')[0],
    }
    openFormModal(215, 'Tahmini Maliyet Düzenle', 'tahmini_maliyetler', 'id', record?.id, extraData, 'Update');
  }, [openFormModal, user]);

  const handleAddBankaSozlesme = useCallback((queryParams) => {
    navigate(`/banka_sozlesme_detay/${queryParams?.maptriksId}`, {
      state: {
        formId: 332,
        title: 'Banka Sözleşme Ekle',
        tableName: 'sozlesmeler',
        uniqueField: 'sozlesme',
        uniqueValue: null, 
        extraData: {
          _override_function: 'Insert'
        }
      }
    });
  }, [navigate]);

  const handleAddYerSozlesme = useCallback((queryParams) => {
    console.log("id", queryParams?.maptriksId);
    navigate(`/yer_sozlesme_detay/${queryParams?.maptriksId}`, {
      state: {
        formId: 333,
        title: 'Yer Sahibi Sözleşme Ekle',
        tableName: 'sozlesmeler',
        uniqueField: 'sozlesme',
        uniqueValue: null,
        extraData: {
          _override_function: 'Insert'
        }
      }
    });
  }, [navigate]);

  const handleUploadFile = useCallback((record) => {
    let rawData = record['LokasyonID'];   
    console.log("rawData", rawData);
    if (typeof rawData === 'string') {
      try {
        rawData = JSON.parse(rawData);
      } catch (e) {
        console.warn("Could not parse LokasyonID string:", e);
      }
    }
    
    let id = null;
    let maptriksName = null;
    let sozlesmeId = null;

    if (rawData && typeof rawData === 'object') {
      const values = Object.values(rawData);
      if (values.length > 0) {
        const innerData = values[0];
        if (innerData && typeof innerData === 'object') {
          id = innerData.lokasyonNo;
          maptriksName = innerData.ad;
          sozlesmeId = innerData.id; 
        }
      }
    }
    const formId = 336;
    if (formId) {
      const extraData = {
        maptriks_name: maptriksName,
        lokasyon: id,
        folder: 'sozlesmeler',
        ekleyen: user?.kullanici_no,
        sozlesme_id: sozlesmeId 
      };
      openFormModal(formId, 'Dosya Yükle', 'dosyalar', 'id', id, extraData, 'Insert');
    } else {
      console.warn('Dosya yükleme formu için formId belirtilmedi');
    }
  }, [openFormModal]);
  
  const handleEditSozlesme = useCallback((record) => {
    const sozlesmeId = record?.ID;
    navigate(`/banka_sozlesme_detay/${sozlesmeId}`, {
      state: {
        formId: 332,
        title: 'Sözleşme Metni',
        tableName: 'sozlesmeler',
        uniqueField: 'sozlesme',
        uniqueValue: sozlesmeId
      }
    });
  }, [navigate]);

  const handleUploadFileYer = useCallback((record) => {
    let rawData = record['MaptriksID'];  
    let sozlesmeId = record['ID'];  
    if (typeof rawData === 'string') {
      try {
        rawData = JSON.parse(rawData);
      } catch (e) {
        console.warn("Could not parse LokasyonID string:", e);
      }
    }
    
    let id = null;
    let maptriksName = null;

    if (rawData && typeof rawData === 'object') {
      const values = Object.values(rawData);
      if (values.length > 0) {
        const innerData = values[0];
        if (innerData && typeof innerData === 'object') {
          id = innerData.MaptriksID;
          maptriksName = innerData.Ünvan;
          sozlesmeId = innerData.ID; 
        }

      }
    }
    const formId = 337;
    if (formId) {
      const extraData = {
        maptriks_name: maptriksName,
        folder: 'sozlesmeler',
        ekleyen: user?.kullanici_no,
        sozlesme_id: sozlesmeId 
      };
      openFormModal(formId, 'Dosya Yükle', 'dosyalar', 'id', id, extraData, 'Insert');
    } else {
      console.warn('Dosya yükleme formu için formId belirtilmedi');
    }
  }, [openFormModal]);

  const handleEditYerSozlesme = useCallback((record) => {
    const sozlesmeId = record?.ID;
    navigate(`/yer_sozlesme_detay/${sozlesmeId}`, {
      state: {
        formId: 333,
        title: 'Sözleşme Metni',
        tableName: 'sozlesmeler',
        uniqueField: 'sozlesmeNo',
        uniqueValue: sozlesmeId
      }
    });
  }, [navigate]);

  const getFilesByType = useCallback((record, tip) => {
    const files = record?.dosyalar || record?.files || record?.Dosyalar || [];
    if (!Array.isArray(files)) return [];
    
    const filteredFiles = files.filter(file => {
      const fileTip = file.tip !== undefined && file.tip !== null ? file.tip : 
                     (file.Tip !== undefined && file.Tip !== null ? file.Tip : 
                     (file.tipID !== undefined && file.tipID !== null ? file.tipID : null));
      
      if (fileTip === null || fileTip === undefined) {
        return false;
      }
      
      const tipNum = parseInt(tip, 10);
      const fileTipNum = parseInt(fileTip, 10);
      
      if (!isNaN(tipNum) && !isNaN(fileTipNum)) {
        return tipNum === fileTipNum;
      }
      
      return (
        fileTip === tip ||
        fileTip === String(tip) ||
        String(fileTip) === String(tip)
      );
    });
    
    return filteredFiles;
  }, []);

  const renderFileButtons = useCallback((record, tip, rootType = 'sozlesme') => {
    const allFiles = record?.dosyalar || record?.files || record?.Dosyalar || [];
    const files = getFilesByType(record, tip);
    
    if (!files || files.length === 0) {
      return null;
    }

    return (
      <div style={{ display: 'flex', gap: '2px', justifyContent: 'center', flexWrap: 'wrap' }}>
        {files.map((dosya, idx) => {
          const color = dosya.asil === 1 
            ? (darkMode ? '#73d13d' : '#52c41a') 
            : (darkMode ? '#ff7875' : '#ff4d4f');
          let filePath = dosya.file || dosya.File || '';
          
          let fileUrl = '#';
          if (filePath) {
            const MINIO_BASE_URL = 'https://dosya.atmturkiye.com';
            const BUCKET_NAME = 'atmpoint';
            const cleanPath = filePath.replace(/^\/+/, '');
            const pathSegments = cleanPath.split('/');
            const encodedSegments = pathSegments.map(segment => encodeURIComponent(segment));
            const encodedPath = encodedSegments.join('/');
            fileUrl = `${MINIO_BASE_URL}/${BUCKET_NAME}/${encodedPath}`;
          }

          let tarihStr = '';
          if (dosya.tarih) {
            try {
              tarihStr = new Date(dosya.tarih).toLocaleDateString('tr-TR');
            } catch (e) {
              tarihStr = dosya.tarih;
            }
          }
          const aciklama = dosya.aciklama || dosya.Aciklama || '';
          const tipAdi = {
            1: 'Sözleşme',
            2: 'Ek Proje',
            3: 'Fesih',
            4: 'Hukuk',
            99: 'Diğer'
          }[tip] || 'Dosya';
          const tooltip = `${tipAdi} - ${tarihStr} ${aciklama}`.trim();

          return (
            <div
              key={idx}
              className="file-btn"
              title={tooltip}
            >
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color, textDecoration: 'none' }}
                onClick={(e) => {
                  if (fileUrl === '#') {
                    e.preventDefault();
                    return;
                  }
                }}
              >
                <DownloadOutlined style={{ fontSize: '10px' }} />
              </a>
              <EditOutlined
                style={{ 
                  fontSize: '10px', 
                  marginLeft: '2px', 
                  cursor: 'pointer',
                  color: darkMode ? '#fff' : undefined
                }}
                onClick={() => {
                  const dosyaId = dosya.id || dosya.ID;
                  if (dosyaId) {
                    openFormModal(
                      rootType === 'sozlesme' ? 336 : 337,
                      'Dosya Düzenle',
                      'dosyalar',
                      'id',
                      dosyaId,
                      {},
                      'Update'
                    );
                  }
                }}
              />
            </div>
          );
        })}
      </div>
    );
  }, [getFilesByType, openFormModal, darkMode]);

  const formatContractDate = useCallback((dateStr, isEndDate = false, columnType = '') => {
    if (!dateStr) return '-';
    if (dateStr === '0000-00-00' || dateStr === '0000/00/00') {
      return '00/00/0000';
    }
    try {
      let date;
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/');

        if (parts[0].length === 4) {
          date = new Date(parts[0], parts[1] - 1, parts[2]);
        } else {
          date = new Date(parts[2], parts[1] - 1, parts[0]);
        }
      } else {
        date = new Date(dateStr);
      }
      if (isNaN(date.getTime())) return dateStr;
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const formattedDate = `${day}/${month}/${year}`;
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);

      const isExpired = checkDate < today;
      if (isEndDate && isExpired) {
        return <span style={{ color: darkMode ? '#ff7875' : '#f44336', fontWeight: 'bold' }}>{formattedDate}</span>;
      }

      return formattedDate;
    } catch (e) {
      return dateStr;
    }
  }, [darkMode]);

  const getContractStatusColor = (status) => {
    const statusMap = {
      'Beklemede': 'blue',
      'İmzalandı': 'green',
      'İptal Edildi': 'red',
      'Süresi Doldu': 'orange',
      'İmzalandı (Yeni)': 'green',
      'İmzalandı (Rotasyon)': 'green',
      'İmzalandı (Söz. Yenileme)': 'green',
      'İptal (Sözleşme Yapılmadı)': 'red',
      'Feshedildi (İptal)': 'red',
      'Feshedildi (Rotasyon)': 'red',
      'Feshedildi (Söz. Yenileme)': 'red',
      'Belediye Kiracısı': 'blue',
      'İmzalanmadı (Aday)': 'orange'
    };
    return statusMap[status] || 'default';
  };

  const getStatusText = (status) => {
    const statusMap = {
      0: "İptal (Sözleşme Yapılmadı)",
      1: "İmzalandı (Yeni)",
      2: "İmzalandı (Rotasyon)",
      3: "Feshedildi (İptal)",
      4: "Feshedildi (Rotasyon)",
      5: "Feshedildi (Söz. Yenileme)",
      6: "İmzalandı (Söz. Yenileme)",
      7: "Belediye Kiracısı",
      99: "İmzalanmadı (Aday)"
    };
    return statusMap[status] || String(status);
  };

  const getModalConfigs = (darkMode) => ({
    'sunumlar': {
      title: 'Sunum Bilgileri',
      columns: [
        { title: 'Banka', dataIndex: 'Banka', key: 'Banka', width: 120 },
        {
          title: 'Tipi',
          dataIndex: 'Tipi',
          key: 'Tipi',
          width: 120,
          render: (tipi, record) => {
            const tipiMap = {
              0: "Yeni Kurulum",
              1: "Rotasyon (Kurulum)",
              2: "Rotasyon (Tahliye)",
              3: "Banka Tahliye",
              4: "İndirim Talebi",
              99: "Diğer",
              "Sunum": "Sunum"
            };

            if (tipi === undefined || tipi === null) return 'Bilinmiyor';
            return tipiMap[tipi] || String(tipi);
          }
        },
        {
          title: 'Durum',
          dataIndex: 'Durum',
          key: 'Durum',
          width: 120,
          render: (status, record) => {
            const statusMap = {
              0: { text: 'Olumsuz', color: 'red' },
              1: { text: 'Olumlu', color: 'green' },
              2: { text: 'Beklemede', color: 'blue' },
              3: { text: 'Gecikmiş', color: 'orange' },
              4: { text: 'Aşırı Geçikmiş', color: 'volcano' }
            };

            if (status === undefined || status === null) {
              return <Tag color="default">Bilinmiyor</Tag>;
            }

            const statusInfo = statusMap[status] || { text: `${status}`, color: 'default' };
            return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
          }
        },
        {
          title: 'Teklif',
          dataIndex: 'Teklif',
          key: 'Teklif',
          width: 100,
          render: (value) => formatMoney(value, 'TRY', { empty: '0,00 ₺' })
        },
        { title: 'Açıklama', dataIndex: 'Açıklama', key: 'Açıklama', width: 200 },
        {
          title: 'Tarih',
          dataIndex: 'Tarih',
          key: 'Tarih',
          width: 100,
          render: (value) => formatContractDate(value, false)
        },
        { title: 'Ekleyen', dataIndex: 'Ekleyen', key: 'Ekleyen', width: 120 },
        {
          title: 'İşlemler',
          key: 'actions',
          width: 78,
          fixed: 'right',
          render: (_, record) => (
            <Space size="small">
              <Button
                size="small"
                className={darkMode ? "btn-hover-dark" : "btn-hover-light"}
                icon={<EditOutlined />}
                onClick={() => handleEditSunum(record)}
              >
                Düzenle
              </Button>
            </Space>
          )
        }
      ]
    },
    'maliyetler': {
      title: 'İnşaat Maliyetleri',
      columns: [
        {
          title: 'Platform',
          dataIndex: 'platform',
          key: 'platform',
          width: 120,
          render: (platform) => {
            // platformID alanı maliyet satırının id'sidir (fatura ekleme rootID'si);
            // görüntüde platform numarası (maliyetler.platform) basılır.
            if (!platform || platform === 'Bilinmiyor' || platform === '0' || platform === 0) {
              return 'Genel';
            }
            return `#${platform}`;
          }
        },
        {
          title: 'Maliyet Cinsi',
          dataIndex: 'masraf_tipi',
          key: 'masraf_tipi',
          width: 120,
          render: (status, record) => {
            const statusMap = {
              'Kabin Maliyeti': { text: 'Kabin Maliyeti', color: darkMode ? '#ff7875' : '#f5222d' },
              'Altyapı': { text: 'Altyapı', color: darkMode ? '#73d13d' : '#52c41a' },
              'Enerji/Data': { text: 'Enerji/Data', color: darkMode ? '#69c0ff' : '#1890ff' },
              'Nakliye': { text: 'Nakliye', color: darkMode ? '#ffa940' : '#fa8c16' },
              'Reklam': { text: 'Reklam', color: darkMode ? '#ff85c0' : '#eb2f96' },
              'Kazı': { text: 'Kazı', color: darkMode ? '#b37feb' : '#722ed1' },
              'Çevre': { text: 'Çevre', color: darkMode ? '#36cfc9' : '#13c2c2' },
              'İlave Maliyet': { text: 'İlave Maliyet', color: darkMode ? '#ff9c6e' : '#fa541c' },
              'Tahliye': { text: 'Tahliye', color: darkMode ? '#85a5ff' : '#2f54eb' },
              'Nakliye (Tahliye)': { text: 'Nakliye (Tahliye)', color: darkMode ? '#bae637' : '#a0d911' },
              'Proje/İhale Gideri': { text: 'Proje/İhale Gideri', color: darkMode ? '#ffc53d' : '#faad14' },
              // 11-16: SAP muhasebe dökümünden gelen masraf tipleri (sql_queries.MASRAF_TIPLERI)
              'Bakım Onarım': { text: 'Bakım Onarım', color: darkMode ? '#95de64' : '#389e0d' },
              'Temizlik': { text: 'Temizlik', color: darkMode ? '#5cdbd3' : '#08979c' },
              'Keşif Maliyeti': { text: 'Keşif Maliyeti', color: darkMode ? '#adc6ff' : '#1d39c4' },
              'Temsil Ağırlama Maliyeti': { text: 'Temsil Ağırlama Maliyeti', color: darkMode ? '#ffadd2' : '#c41d7f' },
              'Lokasyon Personel Maliyeti': { text: 'Lokasyon Personel Maliyeti', color: darkMode ? '#d3adf7' : '#531dab' },
              'Diğer Hizmetler': { text: 'Diğer Hizmetler', color: darkMode ? '#d9d9d9' : '#8c8c8c' }
            };

            if (status === undefined || status === null) {
              return <Tag color="default">Bilinmiyor</Tag>;
            }

            let statusInfo = statusMap[status];

            if (!statusInfo && typeof status === 'string') {
              const numericValue = parseInt(status);
              if (!isNaN(numericValue)) {
                statusInfo = statusMap[numericValue];
              }
            }

            if (!statusInfo) {
              statusInfo = { text: String(status), color: 'default' };
            }
            return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
          }
        },
        {
          title: 'Tutar',
          dataIndex: 'tutar',
          key: 'tutar',
          width: 120,
          render: (value) => formatMoney(value, 'TRY', { empty: '0,00 ₺' })
        },
        {
          title: 'Firma',
          dataIndex: 'firma',
          key: 'firma',
          width: 80,
          render: (firma, record) => {
            if (isPlatformFiyat(record)) {
              return firma || 'Platform';
            }
            return firma || '-';
          }
        },
        {
          title: 'Uygulama Tarihi',
          dataIndex: 'masraf_tarihi',
          key: 'masraf_tarihi',
          width: 120,
          render: (value) => formatContractDate(value, false)
        },
        {
          title: 'Fatura Tarihi',
          dataIndex: 'fatura_tarihi',
          key: 'fatura_tarihi',
          width: 120,
          render: (value) => formatContractDate(value, false)
        },
        {
          title: 'SAT No',
          dataIndex: 'sat_no',
          key: 'sat_no',
          width: 100,
          render: (value, record) => {
            if (isPlatformFiyat(record)) return '-';
            return value || <Tag color="warning">Eksik</Tag>;
          }
        },
        {
          title: 'SAS No',
          dataIndex: 'sas_no',
          key: 'sas_no',
          width: 100,
          render: (value, record) => {
            if (isPlatformFiyat(record)) return '-';
            return value || <Tag color="warning">Eksik</Tag>;
          }
        },
        {
          title: 'Açıklama',
          dataIndex: 'aciklama',
          key: 'aciklama',
          width: 200,
          render: (aciklama, record) => {
            if (isPlatformFiyat(record)) {
              return 'Platform kaydındaki fiyat';
            }
            if (aciklama && aciklama.length > 30) {
              return (
                <Tooltip title={aciklama}>
                  {aciklama.substring(0, 30)}...
                </Tooltip>
              );
            }
            return aciklama || '-';
          }
        },
        {
          title: 'Fatura',
          dataIndex: 'dosya',
          key: 'dosya',
          width: 100,
          render: (dosya, record) => {
            if (dosya) {
              let filePath = dosya;

              if (!dosya.startsWith('/atm/') && !dosya.startsWith('atm/')) {
                filePath = `/atm/${dosya}`;
              } else if (dosya.startsWith('atm/')) {
                filePath = `/${dosya}`;
              }

              return (
                <Button
                  size="small"
                  type="link"
                  icon={<PaperClipOutlined />}
                  onClick={() => window.open(filePath, '_blank')}
                  title={`Dosyayı görüntüle: ${filePath}`}
                >
                  Fatura
                </Button>
              );
            }
            if (isPlatformFiyat(record)) {
              return 'Platform Kaydı';
            }
            return 'Yok';
          }
        },
        {
          title: 'İşlemler',
          key: 'actions',
          width: 220,
          fixed: 'right',
          render: (_, record) => {
            if (isPlatformFiyat(record)) {
              return (
                <Space size="small">
                  <Button
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => handleAddFatura(record)}
                    title="Kabin faturasını bu satıra ekle"
                  >
                    Fatura
                  </Button>
                  <Text style={{ color: '#8c8c8c', fontSize: '12px' }} title="Fiyat platform formundan düzenlenir">
                    Platform Kaydı
                  </Text>
                </Space>
              );
            }

            return (
              <Space size="small">
                <Button
                  size="small"
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => handleAddFatura(record)}
                  style={{
                    backgroundColor: darkMode ? '#52c41a' : '#52c41a',
                    borderColor: darkMode ? '#52c41a' : '#52c41a'
                  }}
                >
                  Fatura Ekle
                </Button>
                <Button
                  size="small"
                  type="default"
                  icon={<EditOutlined />}
                  onClick={() => handleEditInsaatMaliyet(record)}
                  style={{
                    backgroundColor: darkMode ? '#faad14' : '#faad14',
                    borderColor: darkMode ? '#faad14' : '#faad14',
                    color: '#fff'
                  }}
                >
                  Düzenle
                </Button>
              </Space>
            );
          }
        }
      ]
    },
    'faturalar': {
      title: 'Fatura Bilgileri',
      columns: [
        { title: 'Firma', dataIndex: 'Firma', key: 'Firma', width: 120 },
        { title: 'Banka', dataIndex: 'Banka', key: 'Banka', width: 120 },
        { title: 'Kabin', dataIndex: 'Kabin No', key: 'Kabin No', width: 120 },
        {
          title: 'Türü',
          dataIndex: 'Fatura Türü',
          key: 'Fatura Türü',
          width: 150,
          render: (status, record) => {
            const statusMap = {
              1: { text: 'Kira Geliri', color: 'green' },
              2: { text: 'Yeni Kira', color: 'blue' },
              3: { text: 'Yenileme Kira', color: 'orange' },
              4: { text: 'Sözleşme Yenileme', color: 'volcano' },
              5: { text: 'Kabin Geliri', color: 'red' },
              6: { text: 'Kabin Devir', color: 'yellow' },
              7: { text: 'Alt kira Geliri', color: 'purple' },
              8: { text: 'Elektrik', color: 'orange' },
              9: { text: 'Diğer', color: 'red' },
              10: { text: 'Temizlik', color: 'blue' },
              11: { text: 'Ortak Gider', color: 'orange' }
            };

            if (status === undefined || status === null) {
              return <Tag color="default">Bilinmiyor</Tag>;
            }

            let statusInfo;
            if (typeof status === 'number') {
              statusInfo = statusMap[status] || { text: `${status}`, color: 'default' };
            } else {
              const foundEntry = Object.entries(statusMap).find(([key, value]) => value.text === status);
              if (foundEntry) {
                statusInfo = foundEntry[1];
              } else {
                statusInfo = { text: status, color: 'default' };
              }
            }

            return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
          }
        },
        { title: 'Evrak No', dataIndex: 'Evrak No', key: 'Evrak No', width: 120 },
        {
          // 26.10 düzeltmesi: sorgu ve process_faturalar_data alanı 'Tarih' adıyla döndürüyor;
          // dataIndex 'Fatura Tarihi' olduğu için kolon her satırda boştu.
          title: 'Fatura Tarihi',
          dataIndex: 'Tarih',
          key: 'Tarih',
          width: 100,
          defaultSortOrder: 'descend',
          render: (value) => formatContractDate(value, false)
        },
        {
          title: 'Dön. Baş. Tar.',
          dataIndex: 'Dön. Baş. Tar.',
          key: 'Dön. Baş. Tar.',
          width: 100,
          render: (value) => formatContractDate(value, false)
        },
        {
          title: 'Dön. Bit. Tar.',
          dataIndex: 'Dön. Bit. Tar.',
          key: 'Dön. Bit. Tar.',
          width: 100,
          render: (value) => formatContractDate(value, true, 'don_bit_tar')
        },
        {
          title: 'Durum',
          dataIndex: 'Durum',
          key: 'Durum',
          width: 100,
          render: (status, record) => {
            const statusMap = {
              1: { text: 'Kesilecek', color: 'orange' },
              2: { text: 'Kesildi', color: 'red' },
              3: { text: 'Tahsil Edildi', color: 'green' },
            };

            if (status === undefined || status === null) {
              return <Tag color="default">Bilinmiyor</Tag>;
            }

            let statusInfo;
            if (typeof status === 'number') {
              statusInfo = statusMap[status] || { text: `${status}`, color: 'default' };
            } else {
              const foundEntry = Object.entries(statusMap).find(([key, value]) => value.text === status);
              if (foundEntry) {
                statusInfo = foundEntry[1];
              } else {
                statusInfo = { text: status, color: 'default' };
              }
            }

            return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
          }
        },
        {
          // 26.10 düzeltmesi: iade / red / iptal satırları normal fatura gibi görünüyordu
          title: 'Kayıt Tipi',
          dataIndex: 'Kayıt Tipi',
          key: 'Kayıt Tipi',
          width: 100,
          render: (tip, record) => {
            // 27.10: aynı kabin+banka+tür+dönemde birden çok normal fatura (gizlenmez, işaretlenir)
            const ayniDonem = parseInt(record?.['Aynı Dönem'], 10) || 0;
            if (!tip || tip === 'Fatura') {
              return (
                <Space size={2}>
                  <Tag color="default">Fatura</Tag>
                  {ayniDonem > 1 && (
                    <Tooltip title={`Aynı kabin, banka, fatura türü ve dönem için ${ayniDonem} fatura kaydı var`}>
                      <Tag color="orange">Mükerrer?</Tag>
                    </Tooltip>
                  )}
                </Space>
              );
            }
            const renk = { 'İade': 'volcano', 'İade Edildi': 'magenta', 'Red': 'red', 'İptal': 'red' }[tip] || 'orange';
            const iadeTutar = parseFloat(record?.['İade Tutar']) || 0;
            return (
              <Tooltip title={iadeTutar ? `İade tutarı: ${formatMoney(iadeTutar, 'TRY')}` : undefined}>
                <Tag color={renk}>{tip}</Tag>
              </Tooltip>
            );
          }
        },
        {
          title: 'Tutar',
          dataIndex: 'Tutar',
          key: 'Tutar',
          width: 100,
          render: (value) => formatMoney(value, 'TRY', { empty: '0,00 ₺' })
        },
      ]
    },
    'sozlesmeler': {
      title: 'Sözleşme Bilgileri',
      bankaColumns: [
        {
          title: 'Sözl',
          dataIndex: 'sozlesme',
          key: 'sozlesme',
          width: 70,
          render: (_, record) => renderFileButtons(record, 1, 'sozlesme') || null
        },
        {
          title: 'Ek Pro',
          dataIndex: 'ekProje',
          key: 'ekProje',
          width: 70,
          render: (_, record) => renderFileButtons(record, 2, 'sozlesme') || null
        },
        {
          title: 'Fesih',
          dataIndex: 'fesih',
          key: 'fesih',
          width: 70,
          render: (_, record) => renderFileButtons(record, 3, 'sozlesme') || null
        },
        {
          title: 'Hukuk',
          dataIndex: 'hukuk',
          key: 'hukuk',
          width: 70,
          render: (_, record) => renderFileButtons(record, 4, 'sozlesme') || null
        },
        {
          title: 'Diğer',
          dataIndex: 'diger',
          key: 'diger',
          width: 70,
          render: (_, record) => renderFileButtons(record, 99, 'sozlesme') || null
        },
        { title: 'Kabin No', dataIndex: 'Kabin No', key: 'Kabin No', width: 100 },
        { title: 'Sözleşme No', dataIndex: 'Sözleşme No', key: 'Sözleşme No', width: 120 },
        { title: 'Banka', dataIndex: 'banka', key: 'banka', width: 150 },
        {
          title: 'Söz. Baş. Tar.',
          dataIndex: 'Başlangıç',
          key: 'Başlangıç',
          width: 110,
          render: (value) => formatContractDate(value, false)
        },
        {
          title: 'Söz. Bit. Tar.',
          dataIndex: 'Bitiş',
          key: 'Bitiş',
          width: 110,
          render: (value, record) => formatContractDate(value, true, 'soz_bit_tar')
        },
        {
          title: 'Durum',
          dataIndex: 'Durum',
          key: 'Durum',
          width: 140,
          render: (value) => {
            const statusText = getStatusText(value);
            const color = getContractStatusColor(statusText);
            return <Tag color={color}>{statusText}</Tag>;
          }
        },
        {
          title: 'İhtar Süresi',
          dataIndex: 'İhtar Süresi',
          key: 'İhtar Süresi',
          width: 150,
          render: (value, record) => {
            if (!value) return '-';

            let isContractExpired = false;
            if (record.Bitiş) {
              try {
                let endDate;
                if (record.Bitiş.includes('/')) {
                  const parts = record.Bitiş.split('/');
                  if (parts[0].length === 4) {
                    endDate = new Date(parts[0], parts[1] - 1, parts[2]);
                  } else {
                    endDate = new Date(parts[2], parts[1] - 1, parts[0]);
                  }
                } else {
                  endDate = new Date(record.Bitiş);
                }

                if (!isNaN(endDate.getTime())) {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  endDate.setHours(0, 0, 0, 0);
                  isContractExpired = endDate < today;
                }
              } catch (e) {
              }
            }

            const strValue = String(value || '');
            if (strValue.includes('Geçti') || isContractExpired) {
              return <span style={{ color: darkMode ? '#ff7875' : '#f44336', fontWeight: 'bold' }}>{value}</span>;
            } else if (strValue.includes('Kaldı')) {
              const daysLeft = parseInt(strValue.match(/(\d+)/)?.[1] || '0');
              if (daysLeft <= 90) {
                return <span style={{ color: darkMode ? '#ffa940' : '#fa8c16', fontWeight: 'bold' }}>{value}</span>;
              }
            }

            return value;
          }
        },
        {
          title: 'Kira Bed.',
          dataIndex: 'Kira Bedeli',
          key: 'Kira Bedeli',
          width: 120,
          render: (value) => formatMoney(value, 'TRY', { empty: '0,00 ₺' })
        },
        {
          title: 'Kabin Bed.',
          dataIndex: 'Kabin Bedeli',
          key: 'Kabin Bedeli',
          width: 120,
          render: (value) => formatMoney(value, 'TRY', { empty: '0,00 ₺' })
        },
        {
          title: 'Temlik Durumu',
          dataIndex: 'Temlik Durumu',
          key: 'Temlik Durumu',
          width: 120,
          render: (value) => {
            if (value === 'Var') {
              return <Tag color="green">{value}</Tag>;
            } else {
              return <Tag color="red">{value}</Tag>;
            }
          }
        },
        {
          title: 'Detay',
          key: 'actions',
          width: 120,
          render: (_, record) => (
            <Space size="small">
              <Button
                size="small"
                type="success"
                icon={<PlusOutlined />}
                onClick={() => handleUploadFile(record)}
                title="Dosya Yükle"
              />
              {/*<Button
                size="small"
                type="warning"
                icon={<EyeOutlined />}
                onClick={() => handleViewContract(record)}
                title="Sözleşme Metni (Şablon)"
              />*/}
              <Button
                size="small"
                type="primary"
                icon={<EditOutlined />}
                onClick={() => handleEditSozlesme(record)}
                title="Düzenle"
              />
            </Space>
          )
        }
      ],
      yerColumns: [
        {
          title: 'Sözl.',
          dataIndex: 'sozlesme',
          key: 'sozlesme',
          width: 70,
          render: (_, record) => renderFileButtons(record, 1, 'yer-sozlesme') || null
        },
        {
          title: 'Ek Pro.',
          dataIndex: 'ekProje',
          key: 'ekProje',
          width: 70,
          render: (_, record) => renderFileButtons(record, 2, 'yer-sozlesme') || null
        },
        {
          title: 'Fesih',
          dataIndex: 'fesih',
          key: 'fesih',
          width: 70,
          render: (_, record) => renderFileButtons(record, 3, 'yer-sozlesme') || null
        },
        {
          title: 'Hukuk',
          dataIndex: 'hukuk',
          key: 'hukuk',
          width: 70,
          render: (_, record) => renderFileButtons(record, 4, 'yer-sozlesme') || null
        },
        {
          title: 'Diğer',
          dataIndex: 'diger',
          key: 'diger',
          width: 70,
          render: (_, record) => renderFileButtons(record, 99, 'yer-sozlesme') || null
        },
        { title: 'Firma', dataIndex: 'Firma', key: 'Firma', width: 80 },
        { title: 'No', dataIndex: 'No', key: 'No', width: 80 },
        { title: 'Tip', dataIndex: 'Söz. Tip', key: 'Söz. Tip', width: 100 },
        { title: 'Ödeme', dataIndex: 'Ödeme', key: 'Ödeme', width: 90 },
        { title: 'Kabin', dataIndex: 'Kabin', key: 'Kabin', width: 80 },
        { title: 'Ünvan', dataIndex: 'Ünvan', key: 'Ünvan', width: 200 },
        { title: 'Artış', dataIndex: 'Artış', key: 'Artış', width: 120 },
        { title: 'Artış %', dataIndex: 'Artış Oran', key: 'Artış Oran', width: 120 },
        {
          title: 'Kira',
          dataIndex: 'Kira',
          key: 'Kira',
          width: 120,
          render: (value) => formatMoney(value, 'TRY', { empty: '0,00 ₺' })
        },
        { title: 'Döviz', dataIndex: 'Döviz', key: 'Döviz', width: 80 },
        {
          title: 'Başlangıç',
          dataIndex: 'Baş. Tar.',
          key: 'Baş. Tar.',
          width: 110,
          render: (value) => formatContractDate(value, false)
        },
        {
          title: 'Bitiş',
          dataIndex: 'Bit. Tar.',
          key: 'Bit. Tar.',
          width: 110,
          render: (value) => formatContractDate(value, true, 'bit_tar')
        },
        {
          title: 'İmza',
          dataIndex: 'İmza',
          key: 'İmza',
          width: 140,
          render: (value) => {
            const statusText = getStatusText(value);
            const color = getContractStatusColor(statusText);
            return <Tag color={color}>{statusText}</Tag>;
          }
        },
        {
          title: 'İmza Tar.',
          dataIndex: 'İmza Tar.',
          key: 'İmza Tar.',
          width: 110,
          render: (value) => formatContractDate(value, false)
        },
        {
          title: 'Kira Baş.',
          dataIndex: 'Kir. Baş. Tar.',
          key: 'Kir. Baş. Tar.',
          width: 110,
          render: (value) => formatContractDate(value, false)
        },
        {
          title: 'İhtar Süresi',
          dataIndex: 'İhtar Süresi',
          key: 'İhtar Süresi',
          width: 150,
          render: (value, record) => {
            if (!value) return '-';

            let isContractExpired = false;
            if (record['Bit. Tar.']) {
              try {
                let endDate;
                if (record['Bit. Tar.'].includes('/')) {
                  const parts = record['Bit. Tar.'].split('/');
                  if (parts[0].length === 4) {
                    endDate = new Date(parts[0], parts[1] - 1, parts[2]);
                  } else {
                    endDate = new Date(parts[2], parts[1] - 1, parts[0]);
                  }
                } else {
                  endDate = new Date(record['Bit. Tar.']);
                }

                if (!isNaN(endDate.getTime())) {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  endDate.setHours(0, 0, 0, 0);
                  isContractExpired = endDate < today;
                }
              } catch (e) {
              }
            }

            const strValue = String(value || '');
            if (strValue.includes('Geçti') || isContractExpired) {
              return <span style={{ color: darkMode ? '#ff7875' : '#f44336', fontWeight: 'bold' }}>{value}</span>;
            } else if (strValue.includes('Kaldı')) {
              const daysLeft = parseInt(strValue.match(/(\d+)/)?.[1] || '0');
              if (daysLeft <= 90) {
                return <span style={{ color: darkMode ? '#ffa940' : '#fa8c16', fontWeight: 'bold' }}>{value}</span>;
              }
            }

            return value;
          }
        },
        {
          title: 'Detay',
          key: 'actions',
          width: 120,
          render: (_, record) => (
            <Space size="small">
              <Button
                size="small"
                type="success"
                icon={<PlusOutlined />}
                onClick={() => handleUploadFileYer(record)}
                title="Dosya Yükle"
              />
              {/*<Button
                size="small"
                type="warning"
                icon={<EyeOutlined />}
                onClick={() => handleViewContract(record)}
                title="Sözleşme Metni (Şablon)"
              />*/}
              <Button
                size="small"
                type="primary"
                icon={<EditOutlined />}
                onClick={() => handleEditYerSozlesme(record)}
                title="Düzenle"
              />
            </Space>
          )
        }
      ]
    },

  });

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedBank, selectedFaturaTuru, selectedKayitTipi, search]);

  const saveSelectedColumnsToStorage = useCallback((columnKeys, storageKey) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(columnKeys));
    } catch (error) {
      console.error('Error saving selected columns to storage:', error);
    }
  }, []);

  const handleColumnSelectionChange = (checkedValues, setter, storageKey, saveFn) => {
    setter(checkedValues);
    saveFn(checkedValues, storageKey);
  };

  const handleSelectAllColumns = (checked, allColumnKeys, setter, storageKey, saveFn) => {
    if (checked) {
      setter(allColumnKeys);
      saveFn(allColumnKeys, storageKey);
    } else {
      setter([]);
      saveFn([], storageKey);
    }
  };

  const removeColumn = (columnKey, setter, storageKey, saveFn) => {
    setter((prev) => {
      const newSelected = prev.filter((key) => key !== columnKey);
      saveFn(newSelected, storageKey);
      return newSelected;
    });
  };

  const keyName = useMemo(() => {
    if (!apiUrl) return null;
    const match = apiUrl.match(/\/(?:execute|execute-query)\/([^/?]+)/);
    return match ? match[1] : null;
  }, [apiUrl]);

  const modalConfig = useMemo(() => {
    const config = keyName ? getModalConfigs(darkMode)[keyName] : null;
    return config;
  }, [keyName, darkMode]);

  const derivedColumns = useMemo(() => {
    if (modalConfig?.columns) {
      return modalConfig.columns;
    }

    if (columns && columns.length > 0) {
      return columns.map((c) => ({
        ...c,
        render: c.render || ((val) => formatCellValue(val))
      }));
    }
    const looksLikeKeyValue = Array.isArray(items) && items.length > 0 && items.every((r) => r && typeof r === 'object' && 'field' in r && 'value' in r);
    if (looksLikeKeyValue) {
      return [
        { title: 'Alan', dataIndex: 'field', key: 'field' },
        { title: 'Değer', dataIndex: 'value', key: 'value', render: (val) => formatCellValue(val) }
      ];
    }
    return autoInferColumnsFromItems(items);
  }, [modalConfig, columns, items]);

  const normalizeProvidedData = useCallback((input) => {
    if (!input) return [];
    if (Array.isArray(input)) return input;
    if (typeof input === 'object') {
      return Object.entries(input).map(([field, value]) => ({ field, value }));
    }
    return [];
  }, []);

  const fetchData = useCallback(async () => {
    if (data) {
      setError(null);
      setItems(transformData ? transformData(normalizeProvidedData(data)) : normalizeProvidedData(data));
      return;
    }
    if (!apiUrl) return;

    setLoading(true);
    setError(null);
    try {
      let allItems = [];

      if (keyName === 'sozlesmeler') {
        try {
          setSozlesmelerLoading(true);
          const response = await axios.post(`${API_BASE_URL}/list/execute-query/sozlesmeler`, {
            params: queryParams
          });

          const allSozlesmeData = getSafeArrayFromResponse(response?.data, dataKey);

          const bankaData = allSozlesmeData.filter(item => item.sozlesme_tipi === 'banka');
          const yerData = allSozlesmeData.filter(item => item.sozlesme_tipi === 'yer');

          // Fetch files for each contract record
          const maptriksId = queryParams?.maptriksId;
          
          // Fetch files for banka sozlesmeleri
          const bankaDataWithFiles = await Promise.all(
            bankaData.map(async (record) => {
              const recordId = record.ID || record.id;
              if (recordId) {
                try {
                  const filesResponse = await axios.post(
                    `${API_BASE_URL}/list/execute-query/dosyalar-sozlesme`,
                    {
                      params: {
                        root: 'sozlesme',
                        rootID: recordId,
                        lokasyon: maptriksId || ''
                      }
                    },
                    {
                      headers: {
                        ...(localStorage.getItem('auth_token') && {
                          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                        })
                      }
                    }
                  );
                  const files = Array.isArray(filesResponse?.data) ? filesResponse.data : [];
                  return { ...record, dosyalar: files };
                } catch (fileErr) {
                  console.warn(`Could not fetch files for banka sozlesme ${recordId}:`, fileErr);
                  return { ...record, dosyalar: [] };
                }
              }
              return { ...record, dosyalar: [] };
            })
          );

          const yerDataWithFiles = await Promise.all(
            yerData.map(async (record) => {
              const recordId = record.ID || record.id;
              if (recordId) {
                try {
                  const filesResponse = await axios.post(
                    `${API_BASE_URL}/list/execute-query/dosyalar-sozlesme`,
                    {
                      params: {
                        root: 'yer-sozlesme',
                        rootID: recordId,
                        lokasyon: maptriksId || ''
                      }
                    },
                    {
                      headers: {
                        ...(localStorage.getItem('auth_token') && {
                          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                        })
                      }
                    }
                  );
                  const files = Array.isArray(filesResponse?.data) ? filesResponse.data : [];
                  return { ...record, dosyalar: files };
                } catch (fileErr) {
                  console.warn(`Could not fetch files for yer sozlesme ${recordId}:`, fileErr);
                  return { ...record, dosyalar: [] };
                }
              }
              return { ...record, dosyalar: [] };
            })
          );

          setBankaSozlesmeleri(bankaDataWithFiles);
          setYerSozlesmeleri(yerDataWithFiles);
          setSozlesmelerLoading(false);

          allItems = [];

        } catch (queryErr) {
          console.error('Sözleşme query hatası:', queryErr);
          console.error('Hata detayları:', {
            message: queryErr?.message,
            status: queryErr?.response?.status,
            statusText: queryErr?.response?.statusText,
            data: queryErr?.response?.data,
            config: queryErr?.config
          });
          setSozlesmelerLoading(false);
          setError('Sözleşme verileri alınırken hata oluştu');
        }
      } else {
        let response;
        if (apiUrl.includes('/execute-query/')) {
          response = await axios.post(apiUrl, { params: queryParams });
        } else {
          response = await axios.get(apiUrl, queryParams ? { params: queryParams } : undefined);
        }

        const raw = response?.data;
        allItems = getSafeArrayFromResponse(raw, dataKey);
      }

      setItems(transformData ? transformData(allItems) : allItems);
    } catch (err) {
      console.error('API hatası:', err);
      console.error('Hata detayları:', {
        message: err?.message,
        status: err?.response?.status,
        statusText: err?.response?.statusText,
        data: err?.response?.data
      });
      setError(err?.message || 'Veriler getirilirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, queryParams, dataKey, transformData, data, normalizeProvidedData, keyName]);

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, fetchData, refreshTrigger]);

  useEffect(() => {
    if (derivedColumns.length > 0) {
      try {
        const storageKey = `datalist_${keyName || 'default'}_selected_columns`;
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          const validColumns = parsed.filter(key => derivedColumns.some(col => col.key === key));
          // 27.10 düzeltmesi: kayıtlı kolon seçimi olan kullanıcı sonradan eklenen kolonları
          // (ör. faturalar 'Kayıt Tipi') hiç görmüyordu. Bilinen kolonlar ayrı tutulur; yeni
          // gelen kolon bir kez seçime eklenir, kullanıcı sonra kaldırırsa geri gelmez.
          const bilinenKey = `${storageKey}_bilinen`;
          const bilinenHam = localStorage.getItem(bilinenKey);
          const bilinen = bilinenHam ? JSON.parse(bilinenHam) : null;
          const yeniKolonlar = derivedColumns
            .map(col => col.key)
            .filter(key => !parsed.includes(key) && (bilinen ? !bilinen.includes(key) : SONRADAN_EKLENEN_KOLONLAR.includes(key)));
          localStorage.setItem(bilinenKey, JSON.stringify(derivedColumns.map(col => col.key)));
          if (validColumns.length > 0 && yeniKolonlar.length > 0) {
            const birlesik = [...validColumns, ...yeniKolonlar];
            setSelectedColumns(birlesik);
            saveSelectedColumnsToStorage(birlesik, storageKey);
          } else if (validColumns.length > 0) {
            setSelectedColumns(validColumns);
          } else {
            const allColumnKeys = derivedColumns.map(col => col.key);
            setSelectedColumns(allColumnKeys);
            saveSelectedColumnsToStorage(allColumnKeys, storageKey);
          }
        } else {
          const allColumnKeys = derivedColumns.map(col => col.key);
          setSelectedColumns(allColumnKeys);
          saveSelectedColumnsToStorage(allColumnKeys, storageKey);
        }
      } catch (error) {
        console.error('Error loading selected columns from storage:', error);
        const allColumnKeys = derivedColumns.map(col => col.key);
        setSelectedColumns(allColumnKeys);
      }
    }
  }, [derivedColumns, keyName, saveSelectedColumnsToStorage]);

  const visibleColumns = useMemo(() => {
    const cols = derivedColumns;
    if (selectedColumns.length === 0) {
      return cols;
    }
    return cols.filter(col => selectedColumns.includes(col.key));
  }, [derivedColumns, selectedColumns]);

  const bankaColumns = useMemo(() => {
    if (modalConfig?.bankaColumns) {
      return modalConfig.bankaColumns;
    }
    return [];
  }, [modalConfig]);

  const visibleBankaColumns = useMemo(() => {
    if (selectedBankaColumns.length === 0) {
      return bankaColumns;
    }
    return bankaColumns.filter(col => selectedBankaColumns.includes(col.key));
  }, [bankaColumns, selectedBankaColumns]);

  const yerColumns = useMemo(() => {
    if (modalConfig?.yerColumns) {
      return modalConfig.yerColumns;
    }
    return [];
  }, [modalConfig]);

  const visibleYerColumns = useMemo(() => {
    if (selectedYerColumns.length === 0) {
      return yerColumns;
    }
    return yerColumns.filter(col => selectedYerColumns.includes(col.key));
  }, [yerColumns, selectedYerColumns]);

  useEffect(() => {
    if (bankaColumns.length > 0) {
      try {
        const storageKey = `datalist_banka_sozlesmeleri_selected_columns`;
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          const validColumns = parsed.filter(key => bankaColumns.some(col => col.key === key));
          if (validColumns.length > 0) {
            setSelectedBankaColumns(validColumns);
          } else {
            const allColumnKeys = bankaColumns.map(col => col.key);
            setSelectedBankaColumns(allColumnKeys);
            saveSelectedColumnsToStorage(allColumnKeys, storageKey);
          }
        } else {
          const allColumnKeys = bankaColumns.map(col => col.key);
          setSelectedBankaColumns(allColumnKeys);
          saveSelectedColumnsToStorage(allColumnKeys, storageKey);
        }
      } catch (error) {
        console.error('Error loading selected banka columns from storage:', error);
        const allColumnKeys = bankaColumns.map(col => col.key);
        setSelectedBankaColumns(allColumnKeys);
      }
    }
  }, [bankaColumns, saveSelectedColumnsToStorage]);

  useEffect(() => {
    if (yerColumns.length > 0) {
      try {
        const storageKey = `datalist_yer_sozlesmeleri_selected_columns`;
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          const validColumns = parsed.filter(key => yerColumns.some(col => col.key === key));
          if (validColumns.length > 0) {
            setSelectedYerColumns(validColumns);
          } else {
            const allColumnKeys = yerColumns.map(col => col.key);
            setSelectedYerColumns(allColumnKeys);
            saveSelectedColumnsToStorage(allColumnKeys, storageKey);
          }
        } else {
          const allColumnKeys = yerColumns.map(col => col.key);
          setSelectedYerColumns(allColumnKeys);
          saveSelectedColumnsToStorage(allColumnKeys, storageKey);
        }
      } catch (error) {
        console.error('Error loading selected yer columns from storage:', error);
        const allColumnKeys = yerColumns.map(col => col.key);
        setSelectedYerColumns(allColumnKeys);
      }
    }
  }, [yerColumns, saveSelectedColumnsToStorage]);

  const filteredItems = useMemo(() => {
    let filtered = items;

    if (selectedBank && selectedBank !== 'all') {
      if (keyName === 'maliyetler') {
        filtered = filtered.filter(item => item.platform === selectedBank);
      } else {
        filtered = filtered.filter(item => item.Banka === selectedBank);
      }
    }

    if (keyName === 'faturalar' && selectedFaturaTuru && selectedFaturaTuru !== 'all') {
      filtered = filtered.filter(item => item['Fatura Türü'] === selectedFaturaTuru);
    }

    // 26.10 düzeltmesi: iade / red / iptal satırları ayrı süzülebilir
    if (keyName === 'faturalar' && selectedKayitTipi !== 'all') {
      filtered = filtered.filter(item => {
        const normal = !item['Kayıt Tipi'] || item['Kayıt Tipi'] === 'Fatura';
        if (selectedKayitTipi === 'mukerrer') return normal && (parseInt(item['Aynı Dönem'], 10) || 0) > 1;
        return selectedKayitTipi === 'fatura' ? normal : !normal;
      });
    }

    if (search) {
      const q = trLower(search);
      filtered = filtered.filter((row) => {
        try {
          if (row == null) return false;
          if (typeof row !== 'object') return trLower(row).includes(q);
          return Object.values(row).some((v) =>
            v != null && trLower(v).includes(q)
          );
        } catch (_) {
          return false;
        }
      });
    }

    return filtered;
  }, [items, selectedBank, selectedFaturaTuru, selectedKayitTipi, search, keyName]);

  // Fatura modalı özeti: iade/red/iptal satırları toplamdan ayrı tutulur
  const faturaOzeti = useMemo(() => {
    if (keyName !== 'faturalar') return null;
    // 27.10: 'İade Edildi' satırlarında iade çoğunlukla kısmi (iadeTutar < fatura); bu satırlar
    // toplamdan çıkarılmaz, ayrı bir 'iade edildi: fatura / iade / net' bilgisiyle gösterilir.
    let faturaToplam = 0;
    let ayriSayi = 0;
    let iadeEdildiSayi = 0;
    let iadeEdildiFatura = 0;
    let iadeEdildiIade = 0;
    filteredItems.forEach(item => {
      const tip = item['Kayıt Tipi'];
      if (!tip || tip === 'Fatura') {
        faturaToplam += parseFloat(item.Tutar) || 0;
      } else if (tip === 'İade Edildi') {
        iadeEdildiSayi += 1;
        iadeEdildiFatura += parseFloat(item.Tutar) || 0;
        iadeEdildiIade += parseFloat(item['İade Tutar']) || 0;
      } else {
        ayriSayi += 1;
      }
    });
    return { faturaToplam, ayriSayi, iadeEdildiSayi, iadeEdildiFatura, iadeEdildiIade };
  }, [filteredItems, keyName]);

  const filteredBankaSozlesmeleri = useMemo(() => {
    let filtered = [...bankaSozlesmeleri];

    if (search) {
      const q = trLower(search);
      filtered = filtered.filter((row) => {
        try {
          if (row == null) return false;
          if (typeof row !== 'object') return trLower(row).includes(q);
          return Object.values(row).some((v) =>
            v != null && trLower(v).includes(q)
          );
        } catch (_) {
          return false;
        }
      });
    }

    return filtered;
  }, [bankaSozlesmeleri, search]);

  const filteredYerSozlesmeleri = useMemo(() => {
    let filtered = [...yerSozlesmeleri];

    if (search) {
      const q = trLower(search);
      filtered = filtered.filter((row) => {
        try {
          if (row == null) return false;
          if (typeof row !== 'object') return trLower(row).includes(q);
          return Object.values(row).some((v) =>
            v != null && trLower(v).includes(q)
          );
        } catch (_) {
          return false;
        }
      });
    }

    return filtered;
  }, [yerSozlesmeleri, search]);

  // 26.10 düzeltmesi: tabloya eskiden yalnız o sayfanın dilimi veriliyordu; antd sıralaması
  // da yalnız o 10 satırı sıralıyordu. Artık süzülmüş listenin tamamı verilir, sıralama ve
  // sayfalama antd'de yapılır (sayfa taşarsa antd son sayfaya çeker).
  const normalTabloKolonlari = visibleColumns.map(col => ({
    ...siralanabilirKolon(col, filteredItems),
    width: 50,
    showSorterTooltip: false,
    onHeaderCell: () => ({
      onContextMenu: (e) => {
        e.preventDefault();
        removeColumn(col.key, setSelectedColumns, `datalist_${keyName || 'default'}_selected_columns`, saveSelectedColumnsToStorage);
      },
    })
  }));

  const normalTabloSayfalama = filteredItems.length > 0 ? {
    current: currentPage,
    pageSize: pageSize,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'],
    showQuickJumper: true,
    showTotal: (total, range) => {
      if (total === 0) return '0 kayıt';
      return `${range[0]}-${range[1]} / ${total} kayıt`;
    },
    onChange: (page, size) => {
      setCurrentPage(size !== pageSize ? 1 : page);
      setPageSize(size);
    }
  } : false;

  const faturaSatirSinifi = useCallback((record) => (
    keyName === 'faturalar' && record?.['Kayıt Tipi'] && record['Kayıt Tipi'] !== 'Fatura'
      ? 'fatura-ayri-satir' : ''
  ), [keyName]);

  const availableBanks = useMemo(() => {
    if (!items || items.length === 0) return [];

    if (keyName === 'maliyetler') {
      const platforms = [...new Set(items.map(item => item.platform).filter(Boolean))];
      return platforms.sort((a, b) => a.localeCompare(b, 'tr'));
    }

    const banks = [...new Set(items.map(item => item.Banka).filter(Boolean))];
    return banks.sort((a, b) => a.localeCompare(b, 'tr')); 
  }, [items, keyName]);

  const bankFilterOptions = useMemo(() => {
    const options = [
      { value: 'all', label: 'Tümü' }
    ];
    availableBanks.forEach(bank => {
      options.push({ value: bank, label: bank });
    });
    return options;
  }, [availableBanks]);

  const faturaTuruFilterOptions = useMemo(() => {
    if (keyName !== 'faturalar') return [];

    const options = [
      { value: 'all', label: 'Tümü' }
    ];

    const faturaTurleri = [
      { value: 'Kira Geliri', label: 'Kira Geliri' },
      { value: 'Yeni Kira', label: 'Yeni Kira' },
      { value: 'Yenileme Kira', label: 'Yenileme Kira' },
      { value: 'Sözleşme Yenileme', label: 'Sözleşme Yenileme' },
      { value: 'Kabin Geliri', label: 'Kabin Geliri' },
      { value: 'Kabin Devir', label: 'Kabin Devir' },
      { value: 'Alt kira Geliri', label: 'Alt kira Geliri' },
      { value: 'Elektrik', label: 'Elektrik' },
      { value: 'Diğer', label: 'Diğer' },
      { value: 'Temizlik', label: 'Temizlik' },
      { value: 'Ortak Gider', label: 'Ortak Gider' }
    ];

    faturaTurleri.forEach(tur => {
      options.push(tur);
    });

    return options;
  }, [keyName]);

  const computedWidth = width ?? 'calc(100vw - 128px)';
  const tableClassName = useMemo(() => (darkMode ? 'dark-table' : ''), [darkMode]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={computedWidth}
      footer={null}
      centered
      className="data-list-modal"
      title={
        <Space align="center" style={{ width: '100%', justifyContent: 'space-between', marginRight: 10 }}>
          <Text style={{ color: darkMode ? '#fff' : undefined }}>{modalConfig?.title || title}</Text>
          <Space>
            {keyName !== 'sozlesmeler' && derivedColumns.length > 0 && (
              <Dropdown
                open={columnSelectDropdownOpen}
                onOpenChange={setColumnSelectDropdownOpen}
                trigger={['click']}
                dropdownRender={() => (
                  <div style={{
                    padding: '2px',
                    minWidth: '250px',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    backgroundColor: darkMode ? '#1f1f1f' : '#fff',
                    border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`,
                    borderRadius: '6px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                  }}>
                    <div style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}` }}>
                      <Checkbox
                        checked={selectedColumns.length === derivedColumns.length && derivedColumns.length > 0}
                        indeterminate={selectedColumns.length > 0 && selectedColumns.length < derivedColumns.length}
                        onChange={(e) => handleSelectAllColumns(e.target.checked, derivedColumns.map(col => col.key), setSelectedColumns, `datalist_${keyName || 'default'}_selected_columns`, saveSelectedColumnsToStorage)}
                        style={{ color: darkMode ? '#fff' : undefined }}
                      >
                        <Text strong style={{ color: darkMode ? '#fff' : undefined }}>
                          Tümünü Seç
                        </Text>
                      </Checkbox>
                    </div>
                    <Checkbox.Group
                      value={selectedColumns}
                      onChange={(checkedValues) => handleColumnSelectionChange(checkedValues, setSelectedColumns, `datalist_${keyName || 'default'}_selected_columns`, saveSelectedColumnsToStorage)}
                      style={{ width: '100%' }}
                    >
                      <Space direction="vertical" style={{ width: '100%' }}>
                        {derivedColumns.map(column => (
                          <Checkbox
                            key={column.key}
                            value={column.key}
                            style={{
                              width: '100%',
                              color: darkMode ? '#fff' : undefined
                            }}
                          >
                            {column.title}
                          </Checkbox>
                        ))}
                      </Space>
                    </Checkbox.Group>
                  </div>
                )}
              >
                <Button
                  icon={<EyeOutlined />}
                  size="small"
                  style={{
                    backgroundColor: darkMode ? '#1f1f1f' : undefined,
                    borderColor: darkMode ? '#434343' : undefined,
                    color: darkMode ? '#fff' : undefined
                  }}
                >
                  Sütun Seç
                </Button>
              </Dropdown>
            )}
            <Input
              allowClear
              placeholder="Ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              prefix={<SearchOutlined />}
              style={{ width: 220 }}
            />
          </Space>
        </Space>
      }
      styles={{
        body: {
          padding: 12,
          background: darkMode ? '#1b1d22' : '#fff',
          maxHeight: 'calc(100vh - 128px)',
          overflow: 'auto'
        }
      }}
    >
      {error && (
        <Alert
          message="Hata"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {(keyName === 'sunumlar') && (
        <div style={{
          marginBottom: 16,
          padding: '16px',
          background: darkMode ? '#262626' : '#f5f5f5',
          borderRadius: '8px',
          border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap'
          }}>
            <Text style={{
              color: darkMode ? '#fff' : '#262626',
              fontWeight: 600,
              fontSize: '14px',
              minWidth: '120px'
            }}>
              {'🏦 Banka Filtresi:'}
            </Text>
            <Select
              value={selectedBank}
              onChange={setSelectedBank}
              style={{
                width: 200,
                minWidth: '200px'
              }}
              placeholder={"Banka seçin"}
              allowClear={false}
              showSearch
              filterOption={(input, option) =>
                trIncludes(option.label, input)
              }
              dropdownStyle={{
                background: darkMode ? '#1f1f1f' : '#fff',
                border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`
              }}
            >
              {bankFilterOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>

            {/* Sunum İşlemleri Butonları */}
            {keyName === 'sunumlar' && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginLeft: 'auto',
                flexWrap: 'wrap'
              }}>
                {/* Mevcut sunum kontrolü */}
                {queryParams?.sunum && queryParams.sunum.length > 0 ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}>
                    <Text style={{
                      color: darkMode ? '#73d13d' : '#52c41a',
                      fontSize: '12px',
                      fontWeight: 600
                    }}>
                      Sunum Mevcut
                    </Text>
                    <Button
                      type="link"
                      icon={<DownloadOutlined />}
                      onClick={() => handleDownloadPDFSunum(queryParams)}
                      size="small"
                      style={{
                        color: darkMode ? '#73d13d' : '#52c41a',
                        padding: '4px 8px'
                      }}
                    >
                      Sunum İndir
                    </Button>
                  </div>
                ) : (
                  <>
                    <Button
                      type="default"
                      icon={<PlusOutlined />}
                      onClick={() => handleCreatePDFSunum(queryParams)}
                      className={darkMode ? "btn-hover-dark" : "btn-hover-light"}
                      size="small"
                    >
                      PDF Sunum Oluştur
                    </Button>
                    <Button
                      type="default"
                      icon={<DownloadOutlined />}
                      onClick={() => handleDownloadPDFSunum(queryParams)}
                      className={darkMode ? "btn-hover-dark" : "btn-hover-light"}
                      size="small"
                    >
                      Sunum İndir
                    </Button>
                    <Button
                      type="default"
                      icon={<PlusOutlined />}
                      onClick={() => handleCreateSunum(queryParams)}
                      className={darkMode ? "btn-save-dark" : "btn-save-light"}
                      size="small"
                    >
                      Sunum Oluştur
                    </Button>
                  </>
                )}

                <Button
                  type="default"
                  icon={<PlusOutlined />}
                  onClick={() => handleAddTalep(queryParams)}
                  className={darkMode ? "btn-pivot-dark" : "btn-pivot-light"}
                  size="small"
                >
                  Talep Ekle
                </Button>
              </div>
            )}

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginLeft: (keyName === 'sunumlar' || keyName === 'maliyetler') ? '16px' : 'auto'
            }}>
              <Text style={{
                color: darkMode ? '#8c8c8c' : '#595959',
                fontSize: '13px',
                fontWeight: 500
              }}>
                📊 {filteredItems.length} kayıt bulundu
              </Text>
              {selectedBank !== 'all' && (
                <Text style={{
                  color: darkMode ? '#73d13d' : '#52c41a',
                  fontSize: '12px',
                  fontWeight: 600
                }}>
                  (Filtrelenmiş)
                </Text>
              )}
            </div>
          </div>
        </div>
      )}

      {(keyName === 'faturalar') && (
        <div style={{
          marginBottom: 16,
          padding: '16px',
          background: darkMode ? '#262626' : '#f5f5f5',
          borderRadius: '8px',
          border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap'
          }}>
            <Text style={{
              color: darkMode ? '#fff' : '#262626',
              fontWeight: 600,
              fontSize: '14px',
              minWidth: '120px'
            }}>
              🏷️ Fatura Türü Filtresi:
            </Text>
            <Select
              value={selectedFaturaTuru}
              onChange={setSelectedFaturaTuru}
              style={{
                width: 200,
                minWidth: '200px'
              }}
              placeholder="Fatura türü seçin"
              allowClear={false}
              showSearch
              filterOption={(input, option) =>
                trIncludes(option.label, input)
              }
              dropdownStyle={{
                background: darkMode ? '#1f1f1f' : '#fff',
                border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`
              }}
            >
              {faturaTuruFilterOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>

            {/* 26.10 düzeltmesi: filteredItems Banka süzgecini destekliyordu ama seçici
                yalnız sunumlar modalında vardı; iade/red ayrımı da eklendi. */}
            <Select
              value={selectedBank}
              onChange={setSelectedBank}
              style={{ width: 180 }}
              showSearch
              filterOption={(input, option) => trIncludes(option.label, input)}
              options={bankFilterOptions.map(o => ({ ...o, label: o.value === 'all' ? 'Tüm Bankalar' : o.label }))}
            />
            <Select
              value={selectedKayitTipi}
              onChange={setSelectedKayitTipi}
              style={{ width: 180 }}
              options={[
                { value: 'all', label: 'Tüm Kayıtlar' },
                { value: 'fatura', label: 'Yalnız Faturalar' },
                { value: 'mukerrer', label: 'Mükerrer Şüphesi' },
                { value: 'ayri', label: 'İade / Red / İptal' },
              ]}
            />

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginLeft: 'auto'
            }}>
              {faturaOzeti && (
                <Text style={{
                  color: darkMode ? '#d9d9d9' : '#262626',
                  fontSize: '12px'
                }}>
                  Fatura toplamı (yalnız normal faturalar): <b>{formatMoney(faturaOzeti.faturaToplam, 'TRY')}</b>
                  {faturaOzeti.iadeEdildiSayi > 0 && (
                    ` · iade edildi: ${faturaOzeti.iadeEdildiSayi} kayıt, fatura ${formatMoney(faturaOzeti.iadeEdildiFatura, 'TRY')}`
                    + ` − iade ${formatMoney(faturaOzeti.iadeEdildiIade, 'TRY')}`
                    + ` = net ${formatMoney(faturaOzeti.iadeEdildiFatura - faturaOzeti.iadeEdildiIade, 'TRY')}`
                  )}
                  {faturaOzeti.ayriSayi > 0 && ` · iade/red/iptal: ${faturaOzeti.ayriSayi} kayıt (toplama dahil değil)`}
                </Text>
              )}
              <Text style={{
                color: darkMode ? '#8c8c8c' : '#595959',
                fontSize: '13px',
                fontWeight: 500
              }}>
                📊 {filteredItems.length} kayıt bulundu
              </Text>
              {(selectedFaturaTuru !== 'all' || selectedBank !== 'all' || selectedKayitTipi !== 'all') && (
                <Text style={{
                  color: darkMode ? '#73d13d' : '#52c41a',
                  fontSize: '12px',
                  fontWeight: 600
                }}>
                  (Filtrelenmiş)
                </Text>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toplam Maliyet Özeti */}
      {keyName === 'maliyetler' && (
        <div style={{
          marginBottom: 16,
          padding: '16px',
          background: darkMode ? '#1a365d' : '#e6f7ff',
          borderRadius: '8px',
          border: `1px solid ${darkMode ? '#2d3748' : '#91d5ff'}`
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div>
              <Text style={{
                color: darkMode ? '#fff' : '#001529',
                fontWeight: 600,
                fontSize: '16px'
              }}>
                💰 Maliyet Özeti
              </Text>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap'
            }}>
              <Button
                icon={<PlusOutlined />}
                onClick={() => handleAddInsaatMaliyet(queryParams)}
                className={darkMode ? "btn-pivot-dark" : "btn-pivot-light"}
                size="small"
              >
                Masraf Ekle
              </Button>
              <Button
                icon={<FileTextOutlined />}
                onClick={() => handleAddFirmaMaliyet(queryParams)}
                className={darkMode ? "btn-pivot-dark" : "btn-pivot-light"}
                size="small"
              >
                Firma Ekle
              </Button>
              <Button
                icon={<PlusOutlined />}
                onClick={() => handleAddTahminiMaliyet(queryParams)}
                className={darkMode ? "btn-pivot-dark" : "btn-pivot-light"}
                size="small"
              >
                Tahmini Maliyet Ekle
              </Button>
              <Button
                icon={<EditOutlined />}
                onClick={() => handleEditTahminiMaliyet(queryParams)}
                className={darkMode ? "btn-pivot-dark" : "btn-pivot-light"}
                size="small"
              >
                Tahmini Maliyet Düzenle
              </Button>
            </div>
            <div style={{
              display: 'flex',
              gap: '24px',
              flexWrap: 'wrap'
            }}>
              <div style={{ textAlign: 'center' }}>
                <Text style={{
                  color: darkMode ? '#8c8c8c' : '#595959',
                  fontSize: '12px',
                  display: 'block'
                }}>
                  Toplam Kayıt
                </Text>
                <Text style={{
                  color: darkMode ? '#fff' : '#1890ff',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}>
                  {filteredItems.length}
                </Text>
              </div>
              <div style={{ textAlign: 'center' }}>
                <Text style={{
                  color: darkMode ? '#8c8c8c' : '#595959',
                  fontSize: '12px',
                  display: 'block'
                }}>
                  Toplam Maliyet
                </Text>
                <Text style={{
                  color: darkMode ? '#73d13d' : '#52c41a',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}>
                  {formatMoney(filteredItems.reduce((sum, item) => {
                    const tutar = parseFloat(item.tutar) || 0;
                    return sum + tutar;
                  }, 0))}
                </Text>
              </div>
              <div style={{ textAlign: 'center' }}>
                <Text style={{
                  color: darkMode ? '#8c8c8c' : '#595959',
                  fontSize: '12px',
                  display: 'block'
                }}>
                  Güncel Toplam (%15)
                </Text>
                <Text style={{
                  color: darkMode ? '#ffa940' : '#faad14',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}>
                  {formatMoney(filteredItems.reduce((sum, item) => {
                    const tutar = parseFloat(item.tutar) || 0;
                    const tarih = item.masraf_tarihi || item.kurulmaTarihi;
                    if (tarih) {
                      const yil = new Date(tarih).getFullYear();
                      const simdiYil = new Date().getFullYear();
                      const yFark = simdiYil - yil;
                      const yillikFark = 1.15;
                      return sum + (tutar * Math.pow(yillikFark, yFark));
                    }
                    return sum + tutar;
                  }, 0))}
                </Text>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sözleşmeler için iki ayrı tablo */}
      {keyName === 'sozlesmeler' ? (
        <div>
          {/* Sözleşme İşlem Butonları */}
          <div style={{
            marginBottom: 16,
            padding: '16px',
            background: darkMode ? '#1a365d' : '#e6f7ff',
            borderRadius: '8px',
            border: `1px solid ${darkMode ? '#2d3748' : '#91d5ff'}`
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap'
            }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => handleAddBankaSozlesme(queryParams)}
                style={{
                  backgroundColor: darkMode ? '#73d13d' : '#52c41a',
                  borderColor: darkMode ? '#73d13d' : '#52c41a'
                }}
                size="small"
              >
                Banka Sözleşme Ekle
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => handleAddYerSozlesme(queryParams)}
                style={{
                  backgroundColor: darkMode ? '#73d13d' : '#52c41a',
                  borderColor: darkMode ? '#73d13d' : '#52c41a'
                }}
                size="small"
              >
                Yer Sah. Sözleşme Ekle
              </Button>
            </div>
          </div>

          {/* Banka Sözleşmeleri Tablosu */}
          <div style={{ 
            marginBottom: 24,
            width: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              marginBottom: 16,
              padding: '12px 16px',
              background: darkMode ? '#1a365d' : '#e6f7ff',
              borderRadius: '8px',
              border: `1px solid ${darkMode ? '#2d3748' : '#91d5ff'}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Text style={{
                color: darkMode ? '#fff' : '#1890ff',
                fontWeight: 600,
                fontSize: '16px'
              }}>
                🏦 Banka Sözleşmeleri ({filteredBankaSozlesmeleri.length} kayıt)
              </Text>
              {bankaColumns.length > 0 && (
                <Dropdown
                  open={bankaColumnSelectDropdownOpen}
                  onOpenChange={setBankaColumnSelectDropdownOpen}
                  trigger={['click']}
                  dropdownRender={() => (
                    <div style={{
                      padding: '2px',
                      minWidth: '250px',
                      maxHeight: '400px',
                      overflowY: 'auto',
                      backgroundColor: darkMode ? '#1f1f1f' : '#fff',
                      border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`,
                      borderRadius: '6px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}>
                      <div style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}` }}>
                        <Checkbox
                          checked={selectedBankaColumns.length === bankaColumns.length && bankaColumns.length > 0}
                          indeterminate={selectedBankaColumns.length > 0 && selectedBankaColumns.length < bankaColumns.length}
                          onChange={(e) => handleSelectAllColumns(e.target.checked, bankaColumns.map(col => col.key), setSelectedBankaColumns, 'datalist_banka_sozlesmeleri_selected_columns', saveSelectedColumnsToStorage)}
                          style={{ color: darkMode ? '#fff' : undefined }}
                        >
                          <Text strong style={{ color: darkMode ? '#fff' : undefined }}>
                            Tümünü Seç
                          </Text>
                        </Checkbox>
                      </div>
                      <Checkbox.Group
                        value={selectedBankaColumns}
                        onChange={(checkedValues) => handleColumnSelectionChange(checkedValues, setSelectedBankaColumns, 'datalist_banka_sozlesmeleri_selected_columns', saveSelectedColumnsToStorage)}
                        style={{ width: '100%' }}
                      >
                        <Space direction="vertical" style={{ width: '100%' }}>
                          {bankaColumns.map(column => (
                            <Checkbox
                              key={column.key}
                              value={column.key}
                              style={{
                                width: '100%',
                                color: darkMode ? '#fff' : undefined
                              }}
                            >
                              {column.title}
                            </Checkbox>
                          ))}
                        </Space>
                      </Checkbox.Group>
                    </div>
                  )}
                >
                  <Button
                    icon={<EyeOutlined />}
                    size="small"
                    style={{
                      backgroundColor: darkMode ? '#1f1f1f' : undefined,
                      borderColor: darkMode ? '#434343' : undefined,
                      color: darkMode ? '#fff' : undefined
                    }}
                  >
                    Sütun Seç
                  </Button>
                </Dropdown>
              )}
            </div>

            <div style={{ width: '100%', overflowX: 'auto' }}>
              {sozlesmelerLoading ? (
                <Spin spinning={sozlesmelerLoading}>
                  <Table
                    columns={visibleBankaColumns.map(col => ({
                      ...siralanabilirKolon(col, filteredBankaSozlesmeleri),
                      width: 50,
                      showSorterTooltip: false,
                      onHeaderCell: () => ({
                        onContextMenu: (e) => {
                          e.preventDefault();
                          removeColumn(col.key, setSelectedBankaColumns, 'datalist_banka_sozlesmeleri_selected_columns', saveSelectedColumnsToStorage);
                        },
                        onClick: () => {
                          // Sorting can be handled here if needed
                        }
                      })
                    }))}
                    dataSource={filteredBankaSozlesmeleri}
                    rowKey={getRowKey}
                    pagination={false}
                    size="small"
                    bordered
                    scroll={{ x: 'max-content' }}
                    className={tableClassName}
                  />
                </Spin>
              ) : (
                <Table
                  columns={visibleBankaColumns.map(col => ({
                    ...siralanabilirKolon(col, filteredBankaSozlesmeleri),
                    width: 50,
                    showSorterTooltip: false,
                    onHeaderCell: () => ({
                      onContextMenu: (e) => {
                        e.preventDefault();
                        removeColumn(col.key, setSelectedBankaColumns, 'datalist_banka_sozlesmeleri_selected_columns', saveSelectedColumnsToStorage);
                      },
                    })
                  }))}
                  dataSource={filteredBankaSozlesmeleri}
                  rowKey={getRowKey}
                  pagination={false}
                  size="small"
                  bordered
                  scroll={{ x: 'max-content' }}
                  className={tableClassName}
                />
              )}
            </div>
          </div>

          {/* Yer Sözleşmeleri Tablosu */}
          <div style={{ 
            width: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              marginBottom: 16,
              padding: '12px 16px',
              background: darkMode ? '#22543d' : '#f6ffed',
              borderRadius: '8px',
              border: `1px solid ${darkMode ? '#2f855a' : '#b7eb8f'}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Text style={{
                color: darkMode ? '#fff' : '#52c41a',
                fontWeight: 600,
                fontSize: '16px'
              }}>
                🏢 Yer Sözleşmeleri ({filteredYerSozlesmeleri.length} kayıt)
              </Text>
              {yerColumns.length > 0 && (
                <Dropdown
                  open={yerColumnSelectDropdownOpen}
                  onOpenChange={setYerColumnSelectDropdownOpen}
                  trigger={['click']}
                  dropdownRender={() => (
                    <div style={{
                      padding: '2px',
                      minWidth: '250px',
                      maxHeight: '400px',
                      overflowY: 'auto',
                      backgroundColor: darkMode ? '#1f1f1f' : '#fff',
                      border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`,
                      borderRadius: '6px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}>
                      <div style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}` }}>
                        <Checkbox
                          checked={selectedYerColumns.length === yerColumns.length && yerColumns.length > 0}
                          indeterminate={selectedYerColumns.length > 0 && selectedYerColumns.length < yerColumns.length}
                          onChange={(e) => handleSelectAllColumns(e.target.checked, yerColumns.map(col => col.key), setSelectedYerColumns, 'datalist_yer_sozlesmeleri_selected_columns', saveSelectedColumnsToStorage)}
                          style={{ color: darkMode ? '#fff' : undefined }}
                        >
                          <Text strong style={{ color: darkMode ? '#fff' : undefined }}>
                            Tümünü Seç
                          </Text>
                        </Checkbox>
                      </div>
                      <Checkbox.Group
                        value={selectedYerColumns}
                        onChange={(checkedValues) => handleColumnSelectionChange(checkedValues, setSelectedYerColumns, 'datalist_yer_sozlesmeleri_selected_columns', saveSelectedColumnsToStorage)}
                        style={{ width: '100%' }}
                      >
                        <Space direction="vertical" style={{ width: '100%' }}>
                          {yerColumns.map(column => (
                            <Checkbox
                              key={column.key}
                              value={column.key}
                              style={{
                                width: '100%',
                                color: darkMode ? '#fff' : undefined
                              }}
                            >
                              {column.title}
                            </Checkbox>
                          ))}
                        </Space>
                      </Checkbox.Group>
                    </div>
                  )}
                >
                  <Button
                    icon={<EyeOutlined />}
                    size="small"
                    style={{
                      backgroundColor: darkMode ? '#1f1f1f' : undefined,
                      borderColor: darkMode ? '#434343' : undefined,
                      color: darkMode ? '#fff' : undefined
                    }}
                  >
                    Sütun Seç
                  </Button>
                </Dropdown>
              )}
            </div>

            <div style={{ width: '100%', overflowX: 'auto' }}>
              {sozlesmelerLoading ? (
                <Spin spinning={sozlesmelerLoading}>
                  <Table
                    columns={visibleYerColumns.map(col => ({
                      ...siralanabilirKolon(col, filteredYerSozlesmeleri),
                      width: 50,
                      showSorterTooltip: false,
                      onHeaderCell: () => ({
                        onContextMenu: (e) => {
                          e.preventDefault();
                          removeColumn(col.key, setSelectedYerColumns, 'datalist_yer_sozlesmeleri_selected_columns', saveSelectedColumnsToStorage);
                        },
                      })
                    }))}
                    dataSource={filteredYerSozlesmeleri}
                    rowKey={getRowKey}
                    pagination={false}
                    size="small"
                    bordered
                    scroll={{ x: 'max-content' }}
                    className={tableClassName}
                  />
                </Spin>
              ) : (
                <Table
                  columns={visibleYerColumns.map(col => ({
                    ...siralanabilirKolon(col, filteredYerSozlesmeleri),
                    width: 50,
                    showSorterTooltip: false,
                    onHeaderCell: () => ({
                      onContextMenu: (e) => {
                        e.preventDefault();
                        removeColumn(col.key, setSelectedYerColumns, 'datalist_yer_sozlesmeleri_selected_columns', saveSelectedColumnsToStorage);
                      },
                    })
                  }))}
                  dataSource={filteredYerSozlesmeleri}
                  rowKey={getRowKey}
                  pagination={false}
                  size="small"
                  bordered
                  scroll={{ x: 'max-content' }}
                  className={tableClassName}
                />
              )}
            </div>
          </div>
        </div>
      ) : (
        /* Normal tek tablo */
        <>
          {loading ? (
            <Spin spinning={loading}>
              <Table
                columns={normalTabloKolonlari}
                dataSource={filteredItems}
                rowKey={getRowKey}
                rowClassName={faturaSatirSinifi}
                pagination={normalTabloSayfalama}
                size="small"
                scroll={{ x: 'max-content' }}
                bordered
                className={tableClassName}
              />
            </Spin>
          ) : (
            <Table
              columns={normalTabloKolonlari}
              dataSource={filteredItems}
              rowKey={getRowKey}
              rowClassName={faturaSatirSinifi}
              pagination={normalTabloSayfalama}
              size="small"
              scroll={{ x: 'max-content' }}
              bordered
              className={tableClassName}
            />
          )}
        </>
      )}

      {/* Form Modal */}
      {formModalOpen && (
        <Modal
          title={modalTitle}
          open={formModalOpen}
          onCancel={closeFormModal}
          footer={null}
          destroyOnHidden={false}
          width={800}
          styles={{
            body: {
              background: darkMode ? '#1b1d22' : '#fff',
              padding: '16px'
            }
          }}
        >
          <div style={{ 
            maxHeight: 600, 
            overflowY: 'auto',
            background: darkMode ? '#1b1d22' : '#fff'
          }}>
            <FormikForm
              formId={currentFormId}
              uniqueValue={currentUniqueValue}
              uniqueField={currentUniqueField}
              tableName={currentTableName}
              extraData={formExtraData}
              onSuccess={handleFormSuccess}
              onCancel={closeFormModal}
            />
          </div>
        </Modal>
      )}


      {/* CSS Stilleri */}
      <style>{`
        .file-btn {
          border: 1px solid ${darkMode ? '#73d13d' : '#01c851'};
          width: 18px;
          border-radius: 6px;
          background-color: ${darkMode ? '#162312' : '#e3ffde'};
          display: inline-block;
          line-height: 15px;
          margin: 2px;
          text-align: center;
          cursor: pointer;
        }
        .file-btn:hover {
          background-color: ${darkMode ? '#274916' : '#c8e6c9'};
        }
        .red {
          color: ${darkMode ? '#ff7875' : '#f44336'};
        }
        .orange {
          color: ${darkMode ? '#ffa940' : '#ff9800'};
        }
        .contract-expired {
          color: ${darkMode ? '#ff7875' : '#f44336'};
          font-weight: bold;
        }
        .contract-warning {
          color: ${darkMode ? '#ffa940' : '#fa8c16'};
          font-weight: bold;
        }
        .contract-normal {
          color: inherit;
        }
        .status-beklemede {
          color: ${darkMode ? '#69c0ff' : '#1890ff'};
        }
        .status-imzalandi {
          color: ${darkMode ? '#73d13d' : '#52c41a'};
        }
        .status-iptal {
          color: ${darkMode ? '#ff7875' : '#f5222d'};
        }
        .status-suresi-doldu {
          color: ${darkMode ? '#ffa940' : '#fa8c16'};
        }
        /* 26.10 düzeltmesi: sıralama ikonları artık gizlenmiyor (kolonlarda sorter var) */
        .data-list-modal .ant-table-thead > tr > th .ant-table-column-sorter {
          font-size: 8px;
          margin-inline-start: 2px;
        }
        /* İade / red / iptal fatura satırları */
        .data-list-modal .ant-table-tbody > tr.fatura-ayri-satir > td {
          background: ${darkMode ? '#2a1215' : '#fff1f0'} !important;
          color: ${darkMode ? '#bfbfbf' : '#8c8c8c'};
        }
        /* Compact table styles - only inside modal */
        .data-list-modal .ant-table-tbody > tr > td {
          padding: 2px 4px !important;
          white-space: normal !important;
          word-wrap: break-word !important;
          word-break: break-word !important;
          overflow: visible !important;
          font-size: 10px !important;
          line-height: 1.2 !important;
          height: auto !important;
          text-overflow: clip !important;
        }
        .data-list-modal .ant-table-tbody > tr > td > div,
        .data-list-modal .ant-table-tbody > tr > td > span {
          white-space: normal !important;
          word-wrap: break-word !important;
          word-break: break-word !important;
          overflow: visible !important;
          max-width: 100% !important;
          text-overflow: clip !important;
          display: block !important;
        }
        .data-list-modal .ant-table-tbody > tr {
          height: auto !important;
        }
        .data-list-modal .ant-table-tbody > tr > td * {
          white-space: normal !important;
          word-wrap: break-word !important;
          word-break: break-word !important;
        }
        .data-list-modal .ant-table-thead > tr > th {
          padding: 4px 6px !important;
          font-weight: 400 !important;
          background-color: ${darkMode ? '#1f1f1f' : '#fafafa'};
          white-space: nowrap !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          font-size: 10px !important;
          line-height: 1.2 !important;
        }
        .data-list-modal .ant-table-thead > tr > th .ant-btn {
          font-size: 10px;
          height: 16px;
          padding: 0 2px;
        }
      `}</style>
    </Modal>
  );
}